import { useEffect, useRef } from 'react';
import {
  useElectronFullContext,
  useIsElectronContext,
  ElectronContextValue
} from '../context/ElectronContext';

/**
 * Hook principal para acceder a Electron de forma type-safe
 * Permite desestructurar las APIs directamente:
 *
 * @example
 * ```tsx
 * // Desestructurar APIs específicas
 * const { storage, app, isElectron, version } = useElectron();
 *
 * // Usar API específica
 * if (storage) {
 *   await storage.set('key', value);
 * }
 *
 * // Verificar versión
 * const { version, isElectron } = useElectron();
 * console.log(`Running in Electron: ${isElectron}, version: ${version}`);
 * ```
 *
 * @returns Contexto completo de Electron con todas las APIs desestructurables
 */
export function useElectron(): ElectronContextValue {
  return useElectronFullContext();
}

/**
 * Hook para suscribirse a eventos de Electron con cleanup automático
 * @param subscribe Función de suscripción que retorna unsubscribe
 * @param callback Función callback a ejecutar
 * @param deps Dependencias del efecto
 */
export function useElectronListener<T = unknown>(
  subscribe: (callback: (data: T) => void) => (() => void) | void,
  callback: (data: T) => void,
  deps: React.DependencyList = []
) {
  const callbackRef = useRef(callback);

  // Actualizar ref cuando cambie el callback
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    const unsubscribe = subscribe((data) => callbackRef.current(data));

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

/**
 * Hook para manejar el progreso de descarga de actualizaciones
 * @param onProgress Callback para actualización de progreso
 */
export function useUpdateProgress(onProgress: (progress: number) => void) {
  const { updates } = useElectron();

  useElectronListener(
    (callback) => updates?.onDownloadProgress(callback) || (() => { }),
    onProgress,
    [updates]
  );
}

/**
 * Hook para manejar cambios de estado de actualización emitidos por el main process
 * Estados posibles: downloading-started, download-completed, download-error, download-canceled, install-error
 * @param onStatus Callback cuando cambia el estado
 */
export function useUpdateStatus(onStatus: (data: { status: string; version?: string; error?: string }) => void) {
  const { updates } = useElectron();

  useElectronListener(
    (callback) => updates?.onUpdateStatus(callback) || (() => { }),
    onStatus,
    [updates]
  );
}

/**
 * Hook para manejar cambios de fuente de captura de pantalla
 * @param onSourceChange Callback cuando cambia la fuente
 */
export function useScreenCaptureSource(onSourceChange: (sourceId: string) => void) {
  const { screenCapture } = useElectron();

  useElectronListener(
    (callback) => screenCapture?.onSetSource(callback) || (() => { }),
    onSourceChange,
    [screenCapture]
  );
}

/**
 * Hook para verificar si la app está corriendo en Electron
 * Usa el contexto memoizado para mejor rendimiento
 * @returns true si está en Electron, false en caso contrario
 */
export function useIsElectron(): boolean {
  return useIsElectronContext();
}

/**
 * Hook para verificar si la API es legacy (desktop-app antiguo)
 * @returns true si la API es legacy
 */
export function useIsLegacyElectron(): boolean {
  const context = useElectronFullContext();
  return context.isLegacy;
}

// Re-export para acceso directo
export {
  useIsElectronContext,
  useElectronVersion,
  useElectronSupports,
  useElectronFullContext,
  useIsLegacyElectron as useIsLegacyElectronContext,
} from '../context/ElectronContext';
